import React, { useEffect, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Placeholder from 'components/consumer/Placeholder';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ReRenderOnMount from 'components/consumer/ReRenderOnMount';
import scrollToTop from 'commons/scrollToTop';
import scrollIntoViewUtil from 'commons/scrollIntoViewUtil';
import useScreen from 'hooks/useScreen';
import DesktopOnly from 'components/consumer/DesktopOnly';
import MobileAndTabletOnly from 'components/consumer/MobileAndTabletOnly';
import usePDPClearanceData from 'data-hooks/useClearanceData/usePDPClearanceData';
import PDPNotification from '../PDPNotification';
import PDPLeadTimeUI from '../PDPNameSection/PDPLeadTimeUI';
import PDPAlertBar from '../PDPAlertBar';
import useShowStickyButtons from './hooks/useShowStickyButtons';
import PDPAvailableInSizesListItem from '../PDPAvailableInSizes/PDPAvailableInSizesRow/PDPAvailableInSizesListItem';
import PDPAddToBagButton from './PDPAddToBagButton';

const PDPAddToBagSectionUI = ({
  addingToCart,
  addToCartError,
  allowUserToCustomize,
  buildYourOwnDetails = {},
  campaign,
  cartDisabled,
  isDiscontinued = false,
  isModularBundleProduct = false,
  isOutOfStock = false,
  loading,
  mode = 'pdp',
  onAddToCart,
  selectedMaterial,
  showClearanceAlert = false,
  stickyDesktopButtonOnly = false,
}) => {
  const { isMobileOrTablet } = useScreen();
  const addToBagSectionRef = useRef(null);
  const [overrideCustomize, setOverrideCustomize] = useState(false);

  const [
    showClearanceMobileNotification,
    setShowClearanceMobileNotification,
  ] = useState(false);

  const { data: pdpClearanceData } = usePDPClearanceData();

  const isFlashSale =
    pdpClearanceData?.optionsClearanceData?.[selectedMaterial?.sku]
      ?.flashSale || false;

  const clearanceOptions = useMemo(
    () => Object.keys(pdpClearanceData?.optionsClearanceData || {}),
    [pdpClearanceData?.optionsClearanceData]
  );

  useEffect(() => {
    if (stickyDesktopButtonOnly) {
      // If this is the instance of PDPAddToBagSectionUI that renders the
      // sticky Add To Bag button on desktop, we need to use
      // document.querySelector() to detect when the user has scrolled past
      // the main Add To Bag button
      addToBagSectionRef.current = document.querySelector(
        '[data-xc="PDPAddToBagSectionUI"]'
      );
    }
  }, [stickyDesktopButtonOnly]);
  useEffect(() => {
    if (clearanceOptions.length > 0 && selectedMaterial?.sku) {
      const isSelectedOptionOnClearance =
        clearanceOptions.includes(selectedMaterial.sku) || false;
      setShowClearanceMobileNotification(isSelectedOptionOnClearance);
    }
  }, [clearanceOptions, selectedMaterial]);
  const showStickyButtons = useShowStickyButtons(addToBagSectionRef);

  const onCustomizeClick = () => {
    if (isMobileOrTablet) {
      scrollIntoViewUtil('[data-xc="MaterialThumbnailGroup"]');
    } else {
      scrollToTop();
    }
  };

  const onClick = (event, isStickyButton) => {
    if (isStickyButton && allowUserToCustomize && !overrideCustomize) {
      onCustomizeClick();
    } else {
      onAddToCart();
    }
    setOverrideCustomize(true);
  };

  if (stickyDesktopButtonOnly) {
    return (
      <DesktopOnly>
        <div
          className={classNames(
            'transition-opacity ease-in max-[1023px]:hidden whitespace-nowrap',
            {
              invisible: !showStickyButtons,
              visible: showStickyButtons,
            }
          )}
        >
          <PDPAddToBagButton
            addingToCart={addingToCart}
            addToCartError={addToCartError}
            campaign={campaign}
            cartDisabled={cartDisabled}
            loading={loading}
            isCustomizeButton={allowUserToCustomize && !overrideCustomize}
            isStickyButton
            onClick={onClick}
          />
        </div>
      </DesktopOnly>
    );
  }
  if (isDiscontinued) {
    // Empty divs to ensure same number of divs are returned on client and server to prevent SSR bugs
    return (
      <>
        <div />
        <div />
      </>
    );
  }

  return (
    <>
      <div
        className={classNames('PDPAddToBagSectionUI mt-8', {
          'mt-0 md:mt-4': mode === 'quickview',
        })}
        ref={addToBagSectionRef}
        data-xc="PDPAddToBagSectionUI"
      >
        <ReRenderOnMount>
          <PDPAddToBagButton
            addingToCart={addingToCart}
            addToCartError={addToCartError}
            campaign={campaign}
            cartDisabled={cartDisabled}
            loading={loading}
            onClick={onClick}
          />
        </ReRenderOnMount>

        {!isOutOfStock ? (
          <div>
            {loading ? (
              <Placeholder
                type="rect"
                loading
                style={{ height: '12px', marginTop: '20px' }}
              />
            ) : (
              <div className="flex items-center justify-center">
                <PDPLeadTimeUI mode={mode} />
              </div>
            )}
            {!loading && showClearanceAlert && (
              <DesktopOnly>
                <div className="max-[1023px]:hidden">
                  <PDPAlertBar type="info">
                    Clearance item: ineligible for returns except for items
                    arriving damaged & documented by carrier. Ineligible for
                    warranty claims unless reports of defects are submitted
                    within 14 days of delivery, cosmetic damage isn&apos;t
                    covered.
                  </PDPAlertBar>
                </div>
              </DesktopOnly>
            )}
          </div>
        ) : (
          // Empty div to ensure same number of divs are returned on client and server to prevent SSR bugs
          <div />
        )}

        {mode === 'pdp' &&
        isModularBundleProduct &&
        buildYourOwnDetails?.name ? (
          <PDPAvailableInSizesListItem
            dimensions={buildYourOwnDetails.dimensions || ''}
            loading={false}
            id="byo"
            image=""
            isActive={false}
            name={`Build your own ${buildYourOwnDetails.name}`}
            byoCollectionName={buildYourOwnDetails.name}
            price=""
            urlKey={buildYourOwnDetails.urlKey}
            isBYO
          />
        ) : (
          <div />
        )}
        {!isFlashSale && isMobileOrTablet && showClearanceMobileNotification && (
          <PDPNotification
            subheading="This final sale product is eligible for cancellation within 24 hours
            of order placement. Ineligible for returns except for items arriving
            damaged & documented by carrier. Ineligible for warranty claims unless
            reports of defects are submitted within 14 days of delivery, cosmetic
            damage isn't covered."
            onClose={() => setShowClearanceMobileNotification(false)}
            showCloseButton={false}
            baseStyleExtension="mb-4"
          />
        )}
      </div>

      <MobileAndTabletOnly>
        <FixedWidthContainer
          className={classNames(
            'bg-white left-0 right-0 bottom-0 p-4 fixed z-20 lg:hidden',
            {
              'opacity-0 hidden': !showStickyButtons,
              'opacity-100 block': showStickyButtons,
            }
          )}
        >
          <PDPAddToBagButton
            addingToCart={addingToCart}
            addToCartError={addToCartError}
            campaign={campaign}
            cartDisabled={cartDisabled}
            loading={loading}
            isCustomizeButton={allowUserToCustomize && !overrideCustomize}
            isStickyButton
            onClick={onClick}
          />
        </FixedWidthContainer>
      </MobileAndTabletOnly>
    </>
  );
};

PDPAddToBagSectionUI.propTypes = {
  addingToCart: PropTypes.bool,
  addToCartError: PropTypes.string,
  allowUserToCustomize: PropTypes.bool,
  buildYourOwnDetails: PropTypes.shape({
    dimensions: PropTypes.string,
    name: PropTypes.string,
    urlKey: PropTypes.string,
  }),
  cartDisabled: PropTypes.bool,
  showClearanceAlert: PropTypes.bool,
  isDiscontinued: PropTypes.bool,
  isModularBundleProduct: PropTypes.bool,
  isOutOfStock: PropTypes.bool,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['quickview', 'pdp']),
  onAddToCart: PropTypes.func,
  stickyDesktopButtonOnly: PropTypes.bool,
  campaign: PropTypes.string,
  selectedMaterial: PropTypes.shape({
    exclude_from_sale: PropTypes.bool,
    additionalCost: PropTypes.number,
    description: PropTypes.string,
    image: PropTypes.string,
    label: PropTypes.string,
    materialId: PropTypes.number,
    origAddlCost: PropTypes.number,
    upholsteryOptionId: PropTypes.number,
    value: PropTypes.string,
    sku: PropTypes.string,
  }),
};

export default PDPAddToBagSectionUI;
